<script setup lang="ts">
import { computed } from 'vue';
import ApplicationScoreCompactDisplay from '@/components/Conversations/Application/ApplicationScoreCompactDisplay.vue';
import ConversationIndexService from '@/core/conversations/conversation-index/conversation-index.service';
import ProProfileService from '@/core/conversations/pro-profile/pro-profile.service';
import MeService from '@/core/shared/me/me.service';
import { getJobTitle } from '@/core/sourcing/utils/job';
import ChevronLeftIcon from '@/assets/svg/chevron-left.svg?component';

// While `pending` (deep link still resolving) the conversation services hold the
// previous conversation or nothing, so the header must not read them: it paints
// `pendingName` (the tapped Activity row) and skeletons for the rest.
const props = withDefaults(defineProps<{ pending?: boolean; pendingName?: string }>(), {
  pending: false,
  pendingName: '',
});

const emit = defineEmits<{ back: [] }>();

const conversationIndexService = new ConversationIndexService();
const proProfileService = new ProProfileService();
const meService = new MeService();

const candidateName = computed(() =>
  props.pending ? props.pendingName : conversationIndexService.selectedProName,
);
const jobApplicant = computed(() =>
  props.pending ? undefined : proProfileService.selectedProApplication,
);
const score = computed(() => jobApplicant.value?.score);
const role = computed(() =>
  jobApplicant.value?.job ? getJobTitle(jobApplicant.value.job, false).displayTitle : '',
);
const company = computed(() => meService.employer?.name ?? '');

// "Role · Company" — drop either side that is missing so the separator never dangles.
const metaLine = computed(() =>
  props.pending ? '' : [role.value, company.value].filter(Boolean).join(' · '),
);
</script>

<template>
  <header class="flex flex-col bg-shade-860 px-4 pb-2 pt-3" data-test="mobile-candidate-header">
    <div class="flex items-center gap-2 py-2">
      <button
        type="button"
        class="-m-2.5 flex shrink-0 items-center justify-center p-2.5 text-tint-0"
        aria-label="Back"
        data-test="mobile-candidate-header-back"
        @click="emit('back')"
      >
        <ChevronLeftIcon class="back-icon h-6 w-6" />
      </button>

      <p
        v-if="candidateName"
        class="sensitive min-w-0 truncate text-base font-bold text-tint-0"
        data-test="mobile-candidate-header-name"
      >
        {{ candidateName }}
      </p>
      <div
        v-else-if="pending"
        class="h-5 w-36 animate-pulse rounded bg-tint-0/20"
        data-test="mobile-candidate-header-name-skeleton"
      />

      <ApplicationScoreCompactDisplay
        v-if="jobApplicant"
        class="shrink-0"
        hide-job-title
        :score="score"
        data-test="mobile-candidate-header-score"
      />
      <div
        v-else-if="pending"
        class="h-5 w-12 shrink-0 animate-pulse rounded-full bg-tint-0/20"
        data-test="mobile-candidate-header-score-skeleton"
      />
    </div>

    <p
      v-if="metaLine"
      class="truncate text-[11px] leading-normal text-tint-400"
      data-test="mobile-candidate-header-meta"
    >
      {{ metaLine }}
    </p>
    <div
      v-else-if="pending"
      class="h-3 w-48 animate-pulse rounded bg-tint-0/20"
      data-test="mobile-candidate-header-meta-skeleton"
    />
  </header>
</template>

<style scoped lang="postcss">
/* chevron-left.svg ships a hard-coded black stroke; route it through the
   button's text color so the icon reads white on the dark header. */
.back-icon :deep(path) {
  stroke: currentColor;
}
</style>
